import React, { createContext, useState, useContext, useEffect } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

// Define supported languages with names and flags
export const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Spanish", flag: "🇪🇸" },
  { code: "fr", name: "French", flag: "🇫🇷" },
  { code: "de", name: "German", flag: "🇩🇪" },
  { code: "it", name: "Italian", flag: "🇮🇹" },
  { code: "pt", name: "Portuguese", flag: "🇵🇹" },
  { code: "ja", name: "Japanese", flag: "🇯🇵" },
  { code: "ko", name: "Korean", flag: "🇰🇷" },
  { code: "zh", name: "Chinese", flag: "🇨🇳" },
  { code: "ru", name: "Russian", flag: "🇷🇺" },
  { code: "ar", name: "Arabic", flag: "🇸🇦" },
  { code: "hi", name: "Hindi", flag: "🇮🇳" },
  { code: "nl", name: "Dutch", flag: "🇳🇱" },
  { code: "sv", name: "Swedish", flag: "🇸🇪" },
  { code: "pl", name: "Polish", flag: "🇵🇱" },
  { code: "tr", name: "Turkish", flag: "🇹🇷" },
  { code: "he", name: "Hebrew", flag: "🇮🇱" },
  { code: "uk", name: "Ukrainian", flag: "🇺🇦" },
  { code: "vi", name: "Vietnamese", flag: "🇻🇳" },
  { code: "th", name: "Thai", flag: "🇹🇭" },
];

// Get available languages from API
const getAvailableLanguages = async () => {
  return ["en"];
  try {
    const response = await axios.get(`${API_BASE_URL}/translations/languages`);
    return response.data;
  } catch (error) {
    console.error("Error fetching available languages:", error);
    return ["en"];
  }
};

// Create context with default values
const LanguageContext = createContext({
  selectedLanguage: "en",
  setSelectedLanguage: (code) => {}, // Accept a language code parameter
  availableLanguages: ["en"],
  supportedAvailableLanguages: SUPPORTED_LANGUAGES,
  currentLanguage: SUPPORTED_LANGUAGES[0],
  isLoading: false,
});

export const LanguageProvider = ({ children }) => {
  // Get default language from localStorage or use 'en'
  const [selectedLanguage, setSelectedLanguage] = useState(
    localStorage.getItem("selectedLanguage") || "en"
  );
  const [availableLanguages, setAvailableLanguages] = useState(["en"]); // Always include English
  const [isLoading, setIsLoading] = useState(false);

  // Save language preference to localStorage when it changes
  useEffect(() => {
    localStorage.setItem("selectedLanguage", selectedLanguage);
  }, [selectedLanguage]);

  // Load available languages on mount
  useEffect(() => {
    const loadLanguages = async () => {
      setIsLoading(true);
      try {
        const languages = await getAvailableLanguages();
        // Always include English in available languages
        if (!languages.includes("en")) {
          languages.push("en");
        }
        setAvailableLanguages(languages);
      } catch (error) {
        console.error("Error loading available languages:", error);
        // Ensure English is available if API fails
        setAvailableLanguages(["en"]);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguages();
  }, []);

  // Get all supported languages, not just those available in translations
  const supportedAvailableLanguages = SUPPORTED_LANGUAGES;

  // Get details of the currently selected language
  const currentLanguage =
    SUPPORTED_LANGUAGES.find((lang) => lang.code === selectedLanguage) ||
    SUPPORTED_LANGUAGES[0];

  return (
    <LanguageContext.Provider
      value={{
        selectedLanguage,
        setSelectedLanguage,
        availableLanguages,
        supportedAvailableLanguages,
        currentLanguage,
        isLoading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook to use the language context
export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
